class RecentlyViewedTracker {
  static #STORAGE_KEY = 'recently_viewed_products';
  static #MAX_PRODUCTS = 4;
  static #EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

  constructor() {
    this.init();
  }

  async init() {
    // Check if Customer Privacy API is available and preferences are allowed
    if (window.Shopify && window.Shopify.customerPrivacy) {
      if (window.Shopify.customerPrivacy.preferencesProcessingAllowed()) {
        this.trackCurrentProduct();
      }
    } else {
      // Fallback: Load Customer Privacy API and then check permissions
      await this.loadCustomerPrivacyAPI();
    }
  }

  async loadCustomerPrivacyAPI() {
    return new Promise((resolve) => {
      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        (error) => {
          if (!error && window.Shopify.customerPrivacy) {
            if (window.Shopify.customerPrivacy.preferencesProcessingAllowed()) {
              this.trackCurrentProduct();
            }
            // Listen for consent changes
            this.setupConsentListener();
          }
          resolve();
        }
      );
    });
  }

  setupConsentListener() {
    document.addEventListener('visitorConsentCollected', (event) => {
      if (event.detail.preferencesAllowed) {
        this.trackCurrentProduct();
      }
    });
  }

  trackCurrentProduct() {
    const productHandle = window.location.pathname.split('/products/')[1];

    if (productHandle) {
      this.addProductToRecentlyViewed(productHandle);
    }
  }

  addProductToRecentlyViewed(handle) {
    try {
      // Check if preferences processing is allowed before storing data
      if (window.Shopify && window.Shopify.customerPrivacy && 
          !window.Shopify.customerPrivacy.preferencesProcessingAllowed()) {
        return;
      }
      
      // Ensure handle is a string
      if (typeof handle !== 'string') {
        console.error('Recently Viewed Tracker: Handle is not a string:', handle);
        return;
      }
      
      const currentEntries = this.getRecentlyViewed();
      const now = Date.now();
      const existingIndex = currentEntries.findIndex((entry) => entry.handle === handle);

      if (existingIndex > -1) {
        currentEntries.splice(existingIndex, 1);
      }

      currentEntries.unshift({ handle, timestamp: now });

      const limitedEntries = currentEntries.slice(0, RecentlyViewedTracker.#MAX_PRODUCTS);

      localStorage.setItem(RecentlyViewedTracker.#STORAGE_KEY, JSON.stringify(limitedEntries));
    } catch (error) {
      console.error('Recently Viewed Tracker: Error tracking product:', error);
    }
  }

  getRecentlyViewed() {
    try {
      // Check if preferences processing is allowed before reading data
      if (window.Shopify && window.Shopify.customerPrivacy && 
          !window.Shopify.customerPrivacy.preferencesProcessingAllowed()) {
        return [];
      }

      const raw = JSON.parse(localStorage.getItem(RecentlyViewedTracker.#STORAGE_KEY) || '[]');
      const now = Date.now();
      const normalized = [];
      let changed = false;

      if (Array.isArray(raw)) {
        for (const item of raw) {
          let handle = null;
          let timestamp = now;

          if (typeof item === 'string') {
            handle = item;
            changed = true;
          } else if (item && typeof item.handle === 'string') {
            handle = item.handle;
            if (typeof item.timestamp === 'number') {
              timestamp = item.timestamp;
            } else {
              changed = true;
            }
          } else {
            changed = true;
          }

          if (!handle) {
            continue;
          }

          if (now - timestamp > RecentlyViewedTracker.#EXPIRY_MS) {
            changed = true;
            continue;
          }

          normalized.push({ handle, timestamp });
        }
      }

      if (changed) {
        localStorage.setItem(RecentlyViewedTracker.#STORAGE_KEY, JSON.stringify(normalized));
      }

      return normalized;
    } catch (error) {
      console.error('Recently Viewed Tracker: Error getting recently viewed products:', error);
      return [];
    }
  }

  static clearProducts() {
    localStorage.removeItem(RecentlyViewedTracker.#STORAGE_KEY);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new RecentlyViewedTracker());
} else {
  new RecentlyViewedTracker();
}
